import type { CSSProperties } from "react";
import type { RADIUS_PRESETS, SECTION_PATTERNS, THEME_TEXTURES } from "@baze/schema";

export interface Palette {
  id: string;
  name: string;
  background: string;
  text: string;
  primary: string;
  accent: string;
}

/**
 * Hand-tuned palettes named after things owners know. Each passes text/background 4.5:1 and
 * primary/background 3:1, so picking one never triggers a contrast warning.
 */
export const PALETTES: Palette[] = [
  { id: "kopi-susu", name: "Kopi susu", background: "#F4EDE4", text: "#2B1D14", primary: "#8A4B2A", accent: "#D9A441" },
  { id: "daun-pandan", name: "Daun pandan", background: "#EEF3E8", text: "#14241A", primary: "#2F6B3A", accent: "#B8CF5A" },
  { id: "batik-indigo", name: "Batik indigo", background: "#F3F1EC", text: "#191D38", primary: "#2B3A8C", accent: "#C8963E" },
  { id: "rempah", name: "Rempah", background: "#FFF5EA", text: "#2A140C", primary: "#B83A0B", accent: "#E9B949" },
  { id: "kertas-koran", name: "Kertas koran", background: "#F1EFE8", text: "#121212", primary: "#121212", accent: "#E5402A" },
  { id: "laut-senja", name: "Laut senja", background: "#0F1B2D", text: "#EDF2F8", primary: "#FF7A59", accent: "#5CC8D7" },
  { id: "neon-malam", name: "Neon malam", background: "#0B0B10", text: "#F4F4F6", primary: "#C6FF3D", accent: "#FF4FD8" },
  { id: "teh-tarik", name: "Teh tarik", background: "#2A1A12", text: "#F6E9DC", primary: "#E9A86B", accent: "#7FB7A4" },
];

/** Card and button corners for each preset tile (preview only; real values come from resolveTheme). */
export const RADIUS_PREVIEW: Record<(typeof RADIUS_PRESETS)[number], { card: string; button: string }> = {
  sharp: { card: "0", button: "1px" },
  soft: { card: "4px", button: "3px" },
  round: { card: "9px", button: "6px" },
  pill: { card: "12px", button: "999px" },
};

const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.35'/%3E%3C/svg%3E\")";

const ink = (pct: number) => `color-mix(in srgb, currentColor ${pct}%, transparent)`;

/** Small swatches of the engine's site textures, drawn over the site background in its text colour. */
export const TEXTURE_PREVIEW: Record<(typeof THEME_TEXTURES)[number], CSSProperties> = {
  none: {},
  grain: { backgroundImage: NOISE, backgroundBlendMode: "overlay" },
  grid: { backgroundImage: `linear-gradient(${ink(14)} 1px, transparent 1px), linear-gradient(90deg, ${ink(14)} 1px, transparent 1px)`, backgroundSize: "10px 10px" },
  dots: { backgroundImage: `radial-gradient(${ink(30)} 1px, transparent 1.4px)`, backgroundSize: "7px 7px" },
  aurora: { backgroundImage: "radial-gradient(70% 60% at 20% 20%, #7dd3fc88, transparent), radial-gradient(60% 60% at 85% 70%, #f0abfc88, transparent)" },
  paper: { backgroundImage: `${NOISE}, linear-gradient(135deg, ${ink(4)}, transparent)` },
  halftone: { backgroundImage: `radial-gradient(${ink(40)} 1.4px, transparent 1.8px)`, backgroundSize: "5px 5px" },
  night: { backgroundImage: "radial-gradient(1px 1px at 20% 30%, #fff, transparent), radial-gradient(1px 1px at 70% 20%, #fff, transparent), radial-gradient(1.5px 1.5px at 50% 70%, #fff, transparent), linear-gradient(#0b1026, #1c1a40)" },
};

/** Section pattern tiles, matching the engine's [data-pattern] layers. */
export const PATTERN_PREVIEW: Record<(typeof SECTION_PATTERNS)[number], CSSProperties> = {
  none: {},
  dots: { backgroundImage: `radial-gradient(${ink(32)} 1px, transparent 1.5px)`, backgroundSize: "8px 8px" },
  grid: { backgroundImage: `linear-gradient(${ink(16)} 1px, transparent 1px), linear-gradient(90deg, ${ink(16)} 1px, transparent 1px)`, backgroundSize: "12px 12px" },
  lines: { backgroundImage: `repeating-linear-gradient(-45deg, ${ink(18)} 0 1px, transparent 1px 6px)` },
  noise: { backgroundImage: NOISE },
};
